import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getCategories, CategoryData } from '../services/categoryService';

interface CategoriesContextType {
  categories: CategoryData[];
  loading: boolean;
  error: string | null;
  refreshCategories: () => Promise<void>;
}

const CategoriesContext = createContext<CategoriesContextType | undefined>(undefined);

export const CategoriesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [categories, setCategories] = useState<CategoryData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetching categories
      const data = await getCategories({ includeCounts: true }); // Include deal counts
      // Categories fetched successfully
      setCategories(data);
    } catch (err) {
      console.error('🏷️ CategoriesContext: Error fetching categories:', err);
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load categories on mount
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const refreshCategories = useCallback(async () => {
    await fetchCategories();
  }, [fetchCategories]);

  const contextValue = React.useMemo(
    () => ({
      categories,
      loading,
      error,
      refreshCategories,
    }),
    [categories, loading, error, refreshCategories]
  );

  return <CategoriesContext.Provider value={contextValue}>{children}</CategoriesContext.Provider>;
};

export const useCategories = () => {
  const context = useContext(CategoriesContext);
  if (!context) {
    throw new Error('useCategories must be used within a CategoriesProvider');
  }
  return context;
};